import { useEffect, useMemo, useState } from "react";

import { DetailSection } from "@/components/analysisHistory/DetailSection";
import { EmptyState } from "@/components/analysisHistory/EmptyState";
import { FilterSection } from "@/components/analysisHistory/FilterSection";
import { HistoryCard } from "@/components/analysisHistory/HistoryCard";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import Pagination from "@/components/ui/pagination";
import { EMPTY_STATE_ANALYSIS_HISTORY } from "@/constants/message";
import { mockAnalysisHistory } from "@/mocks/apis/analysis/data";
import { TimeFilterValue } from "@/types/analysis";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const ITEMS_PER_PAGE = 10;
enum EmptyFlag {
  EMPTY_LIST = "EMPTY_LIST",
  SEARCH_NOT_FOUND = "SEARCH_NOT_FOUND",
  FILTER_NOT_FOUND = "FILTER_NOT_FOUND",
  HAVE_LIST_NOT_SELECTED = "HAVE_LIST_NOT_SELECTED"
}

export default function AnalysisHistoryPage() {
  const allData = mockAnalysisHistory;

  const [selectedData, setSelectedData] = useState<string | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Filter state
  const [searchValue, setSearchValue] = useState("");
  const [authorValue, setAuthorValue] = useState<{
    label: string;
    value: string;
  } | null>({
    label: "All",
    value: "all"
  });
  const [timeValue, setTimeValue] = useState<{
    label: string;
    value: string;
    range?: { startDate: string; endDate: string };
  } | null>({
    label: "Today",
    value: "day"
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const authorOptions = useMemo(() => {
    const authors = Array.from(
      new Set(allData.map((d) => d.created_by))
    ).filter(Boolean);
    return [
      { label: "All", value: "all" },
      ...authors.map((a) => ({ label: a, value: a }))
    ];
  }, [allData]);

  const handleSelectJob = (id: string) => {
    if (id === selectedData) return;
    setIsLoadingDetail(true);
    setSelectedData(null);
    setTimeout(() => {
      setSelectedData(id);
      setIsLoadingDetail(false);
    }, 800);
  };

  const updateFilter =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      setter(value);
      setCurrentPage(1);
      setSelectedData(null);
    };

  // Filter logic
  const filteredData = useMemo(() => {
    return allData.filter((job) => {
      const matchesSearch =
        !searchValue ||
        job.name.toLowerCase().includes(searchValue.toLowerCase());

      const matchesAuthor =
        !authorValue ||
        authorValue.value === "all" ||
        job.created_by === authorValue.value;

      const matchesTime = (() => {
        if (!timeValue) return true;

        const createdAt = dayjs(job.created_at);

        // Today (value === "day")
        if (timeValue.value === TimeFilterValue.Day) {
          return createdAt.isSame(dayjs(), "day");
        }

        // Last 7 days
        if (timeValue.value === TimeFilterValue.Last7Days) {
          return createdAt.isAfter(dayjs().subtract(7, "day"));
        }

        // Last 1 month
        if (timeValue.value === TimeFilterValue.Last1Month) {
          return createdAt.isAfter(dayjs().subtract(1, "month"));
        }

        // Last 3 months
        if (timeValue.value === TimeFilterValue.Last3Months) {
          return createdAt.isAfter(dayjs().subtract(3, "month"));
        }

        // Custom range
        if (timeValue.value === TimeFilterValue.Custom && timeValue.range) {
          const { startDate, endDate } = timeValue.range;
          return createdAt.isBetween(startDate, endDate, "day", "[]");
        }

        return true;
      })();

      return matchesSearch && matchesAuthor && matchesTime;
    });
  }, [allData, searchValue, authorValue, timeValue]);

  // Pagination
  const totalPages = Math.ceil((filteredData.length || 1) / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  const selectedJob = allData.find((item) => item.id === selectedData);

  const listEmptyFlag = useMemo(() => {
    if (allData.length === 0) {
      return EmptyFlag.EMPTY_LIST;
    }

    const noSearch = searchValue.trim() !== "" && filteredData.length === 0;
    if (noSearch) return EmptyFlag.SEARCH_NOT_FOUND;

    const noFilter =
      searchValue.trim() === "" &&
      (authorValue?.value !== "all" || timeValue?.value !== "all");
    if (noFilter) return EmptyFlag.FILTER_NOT_FOUND;

    return EmptyFlag.EMPTY_LIST;
  }, [allData, filteredData, searchValue, authorValue, timeValue]);

  const detailEmptyFlag = useMemo(() => {
    if (paginatedData.length === 0) return EmptyFlag.EMPTY_LIST;

    if (!selectedJob) return EmptyFlag.HAVE_LIST_NOT_SELECTED;

    return EmptyFlag.EMPTY_LIST;
  }, [paginatedData, selectedJob]);

  // Simulate loading list
  useEffect(() => {
    setIsLoadingList(true);
    const timer = setTimeout(() => {
      setIsLoadingList(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col p-6 h-full gap-4 overflow-hidden">
      <h1 className="text-2xl font-bold">Historical Analysis</h1>

      <div className="grid h-full gap-4 grid-cols-[340px_1fr] lg:grid-cols-[464px_1fr] overflow-hidden">
        {/* LEFT PANEL */}
        <div className="flex flex-col h-full overflow-hidden border-whisper pt-2">
          {/* Filter */}
          <FilterSection
            searchValue={searchValue}
            onSearchChange={updateFilter(setSearchValue)}
            authorValue={authorValue}
            onAuthorSelect={updateFilter(setAuthorValue)}
            authorOptions={authorOptions}
            timeValue={timeValue}
            onTimeSelect={updateFilter(setTimeValue)}
            disabled={isLoadingList || allData.length === 0}
          />

          <LoadingWrapper
            isLoading={isLoadingList}
            loadingClassName="flex flex-col items-center justify-center text-center text-sm text-dim-gray gap-2 h-full"
            loadingText={
              <p className="text-txtPlaceholder text-xs">Loading...</p>
            }
          >
            <div className="flex flex-col flex-1 overflow-y-auto mt-4 gap-4 pr-1">
              <div className="text-xs text-txtPlaceholder">
                Showing {paginatedData.length} of {filteredData.length}{" "}
                Historical Analyses
              </div>

              {paginatedData.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {paginatedData.map((data) => (
                    <HistoryCard
                      key={data.id}
                      data={data}
                      isSelected={selectedData === data.id}
                      onSelect={() => handleSelectJob(data.id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title={EMPTY_STATE_ANALYSIS_HISTORY.LIST[listEmptyFlag].title}
                  description={
                    EMPTY_STATE_ANALYSIS_HISTORY.LIST[listEmptyFlag].desc
                  }
                />
              )}
            </div>
          </LoadingWrapper>

          {/* Pagination pinned at bottom */}
          <div className="flex-shrink-0 border-whisper mt-4 bg-white flex items-center justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={isLoadingList ? 1 : totalPages}
              onPageChange={(value) => {
                setCurrentPage(value);
                setSelectedData(null);
              }}
            />
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex flex-col h-full overflow-hidden pt-2">
          <DetailSection
            isLoading={isLoadingDetail}
            data={selectedJob}
            emptyMessage={EMPTY_STATE_ANALYSIS_HISTORY.DETAIL[detailEmptyFlag]}
          />
        </div>
      </div>
    </div>
  );
}
